/**
 * tableExport.js
 *
 * Extracts tabular data out of the hast nodes that react-markdown builds for
 * GFM tables, and turns that data into a CSV or XLSX download.
 *
 * Why hast rather than the raw markdown string: by the time a table reaches the
 * custom `table` component, remark-gfm has already resolved alignment rows,
 * escaped pipes, and inline formatting. Re-parsing the markdown source would
 * mean reimplementing all of that and still disagreeing with what the user sees
 * on screen. Walking the node tree yields exactly the rendered table.
 */

// Excel (and LibreOffice) treat a leading =, +, -, or @ as the start of a
// formula. Table content originates from an LLM relaying tool output, so a cell
// like "=HYPERLINK(...)" would execute on open, a CSV-injection vector. We
// prefix those cells with a single quote, which Excel strips on display while
// keeping the value inert. A lone "-" or a negative number is left alone; only
// text that could parse as a formula is guarded.
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

const isNumericLiteral = (value) => /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(value);

export const neutralizeFormula = (value) => {
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }
  if (!FORMULA_PREFIXES.includes(value[0])) {
    return value;
  }
  // "-12.5" is data, not a formula, leave genuine numbers untouched so the
  // spreadsheet still types them as numeric.
  if (isNumericLiteral(value)) {
    return value;
  }
  return `'${value}`;
};

/**
 * Recursively collects the visible text of a hast node.
 *
 * Block-level children inside a cell (a nested list, a <br>) are joined with a
 * space so a multi-line cell collapses to one readable value rather than
 * concatenating words together.
 */
const nodeToText = (node) => {
  if (!node) {
    return '';
  }
  if (node.type === 'text') {
    return node.value || '';
  }
  if (node.tagName === 'br') {
    return ' ';
  }
  if (!Array.isArray(node.children)) {
    return '';
  }

  // Children are joined with a space when the source positions show a gap
  // between them. This matters for cells written as "Multi<br>line": with no
  // rehype-raw in the pipeline, remark-rehype drops the raw <br> element
  // entirely and leaves two adjacent text nodes, which a plain join('') would
  // fuse into "Multiline". Comparing offsets recovers the separation without
  // inventing whitespace between genuinely contiguous nodes (e.g. the "**bold**"
  // and "-suffix" halves of "**bold**-suffix").
  return node.children
    .reduce((acc, child) => {
      const text = nodeToText(child);
      if (!text) {
        return acc;
      }
      const prev = acc[acc.length - 1];
      if (prev && prev.end != null && child.position?.start?.offset > prev.end) {
        acc.push({ text: ` ${text}`, end: child.position?.end?.offset ?? null });
      } else {
        acc.push({ text, end: child.position?.end?.offset ?? null });
      }
      return acc;
    }, [])
    .map((entry) => entry.text)
    .join('');
};

const collectRows = (node, rows) => {
  if (!node || !Array.isArray(node.children)) {
    return;
  }

  node.children.forEach((child) => {
    if (child.tagName === 'tr') {
      const cells = (child.children || [])
        .filter((cell) => cell.tagName === 'th' || cell.tagName === 'td')
        .map((cell) => nodeToText(cell).replace(/\s+/g, ' ').trim());
      if (cells.length) {
        rows.push(cells);
      }
      return;
    }
    // thead / tbody / tfoot wrappers, descend into them.
    collectRows(child, rows);
  });
};

/**
 * Converts a react-markdown `table` hast node into a rectangular array of
 * strings. Returns [] when the node holds no usable rows.
 *
 * Rows are padded to the widest row so downstream writers never emit a ragged
 * sheet (Excel renders a ragged row as merged/blank cells, which looks broken).
 */
export const extractTableRows = (tableNode) => {
  const rows = [];
  collectRows(tableNode, rows);

  if (!rows.length) {
    return [];
  }

  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  return rows.map((row) =>
    row.length === width ? row : [...row, ...Array(width - row.length).fill('')]
  );
};

/** True when the table has at least one data row beyond the header. */
export const hasExportableData = (rows) => Array.isArray(rows) && rows.length > 0;

// ── CSV ────────────────────────────────────────────────────────────────────

const csvEscape = (value) => {
  const safe = neutralizeFormula(String(value ?? ''));
  // Quote when the value contains a delimiter, a quote, or any newline;
  // embedded quotes are doubled per RFC 4180.
  if (/[",\r\n]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
};

export const rowsToCsv = (rows) =>
  rows.map((row) => row.map(csvEscape).join(',')).join('\r\n');

// ── Download plumbing ──────────────────────────────────────────────────────

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking synchronously can cancel the download in Safari, so defer it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Builds a filesystem-safe filename stem, e.g. "assistant-table-2".
 * Falls back to a generic stem when the caller has no better label.
 */
export const buildTableFilename = (label, index, extension) => {
  const stem = (label || 'table')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'table';
  const suffix = typeof index === 'number' && index > 0 ? `-${index + 1}` : '';
  return `${stem}${suffix}.${extension}`;
};

export const downloadCsv = (rows, filename) => {
  // The BOM makes Excel read the file as UTF-8; without it, accented and
  // non-Latin characters are mangled on Windows.
  const blob = new Blob(['﻿', rowsToCsv(rows)], {
    type: 'text/csv;charset=utf-8;',
  });
  triggerDownload(blob, filename);
};

/**
 * SheetJS is ~400 KB, far too large to sit in the main bundle for a feature
 * most sessions never touch, so it is loaded on first use.
 */
export const downloadXlsx = async (rows, filename, sheetName = 'Sheet1') => {
  const XLSX = await import('xlsx');
  const guarded = rows.map((row) => row.map((cell) => neutralizeFormula(String(cell ?? ''))));
  const worksheet = XLSX.utils.aoa_to_sheet(guarded);
  const workbook = XLSX.utils.book_new();
  // Excel rejects sheet names over 31 chars or containing []:*?/\
  const safeSheet = (sheetName || 'Sheet1').replace(/[[\]:*?/\\]/g, '').slice(0, 31) || 'Sheet1';
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheet);
  XLSX.writeFile(workbook, filename);
};
